import type { QrCode } from "@repo/api";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import QRDialog from "~/components/admin/qr/qr-dialog";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { api } from "~/lib/api";

export const Route = createFileRoute("/_main/admin/qr/")({
  component: QRDashboard,
});

function QRDashboard() {
  const { t } = useTranslation("common");
  const { data: qrCodes } = api.qr.getQrCodes.useQuery();
  const [open, setOpen] = useState(false);
  const [selectedQr, setSelectedQr] = useState<QrCode | undefined>();
  const columns: ColumnDef<QrCode>[] = [
    {
      id: t("Admin.QR.tag"),
      accessorKey: "tag",
      filterFn: "includesString",
      size: 10,

      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Admin.QR.tag")} />
      ),
      cell: ({ row: { original: qr } }) => {
        return <p>{qr.tag}</p>;
      },
    },
    {
      id: t("Admin.QR.target"),
      accessorKey: "target",
      filterFn: "includesString",
      size: 10,

      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Admin.QR.target")} />
      ),
      cell: ({ row: { original: qr } }) => {
        return <p>{qr.target}</p>;
      },
    },
    {
      id: t("Admin.QR.scans"),
      accessorKey: "scans",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Admin.QR.scans")} />
      ),
      cell: ({ row: { original: qr } }) => {
        return <p>{qr.scans}</p>;
      },
    },
    {
      id: t("Admin.QR.last_scan"),
      accessorKey: "last_scan",
      filterFn: "includesString",
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.original.lastScan;
        const dateB = rowB.original.lastScan;

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateB.getTime() - dateA.getTime();
      },
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Admin.QR.last_scan")}
        />
      ),
      cell: ({ row: { original: qr } }) => {
        if (!qr.lastScan) return <p>-</p>;
        return <p>{format(qr.lastScan, "Pp")}</p>;
      },
    },
    {
      id: t("Admin.QR.reference"),
      accessorKey: "reference",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Admin.QR.reference")}
        />
      ),
      cell: ({ row: { original: qr } }) => {
        return <p>{qr.reference}</p>;
      },
    },
    {
      id: t("Admin.QR.company_name"),
      accessorKey: "companyName",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Admin.QR.company_name")}
        />
      ),
      cell: ({ row: { original: qr } }) => {
        return <p>{qr.companyName}</p>;
      },
    },
  ];
  return (
    <div className="flex flex-col gap-4">
      <SettingsHeader
        title={t("Admin.QR.page_title")}
        buttonLabel="New QR Code"
        buttonAction={() => {
          setSelectedQr(undefined);
          setOpen(true);
        }}
      />
      <DataTable
        className="h-[500px] max-h-[500px]"
        onSelect={(row) => {
          setSelectedQr(row);
          setOpen(true);
        }}
        columns={columns}
        data={qrCodes ?? []}
      />
      <QRDialog
        onClose={() => {
          setOpen(false);
        }}
        qrCode={selectedQr}
        open={open}
      />
    </div>
  );
}
